import type { components } from '../../../lib/api/schema.gen'

/**
 * UID-155 (B3) — the durable Part Attachments UI (Parts Library epic UID-3,
 * wave 2). Mirrors the frozen B0 contract (`schema.gen.ts`, generated from
 * `apps/api/openapi/paths/parts.yaml` + `components/part-attachments.yaml`)
 * 1:1 — no reshaping needed, so these are direct re-exports/aliases rather
 * than a hand-maintained parallel shape.
 *
 * SECURITY INVARIANT (do not regress): every one of these types is
 * KEYLESS — there is no `objectKey` field anywhere in this module, because
 * the contract never returns one (the S3 object key is server-only, C2).
 * Nothing in this vertical may render, log, or store an object key.
 */
export type PartAttachmentFileKind = components['schemas']['PartAttachmentFileKind']
export type PartAttachment = components['schemas']['PartAttachment']
export type PartAttachmentPresignTicket = components['schemas']['PartAttachmentPresignTicket']

export const PART_ATTACHMENT_FILE_KIND_LABEL: Record<PartAttachmentFileKind, string> = {
  technical_drawing: 'Technical drawing',
  model: '3D model',
  specification: 'Specification',
  other: 'File',
}

/** Client-side-only allowlist for the "Technical drawing (optional)" control
 * (AC1) — `.pdf` only, matching the prototype (`Fictiv Platform.dc.html`
 * ~L4989 `<input type="file" accept=".pdf">`). The server enforces its own
 * per-`fileKind` content-type allowlist independently (415 on mismatch) —
 * this is purely a fast, clear client-side rejection before a presign
 * round-trip is even attempted. */
export const TECHNICAL_DRAWING_ACCEPT = '.pdf'
export const TECHNICAL_DRAWING_CONTENT_TYPE = 'application/pdf'

/** A local, not-yet-persisted file selection — exists only between "user
 * picked a file" and "the server confirmed the attachment exists" (AC3's
 * "clear/remove affordance" applies ONLY to this stage; there is no
 * delete-attachment endpoint in B0, so a persisted `PartAttachment` can
 * never be removed from this UI). */
export type PendingAttachmentStatus = 'validating' | 'uploading' | 'persisting' | 'error'

export interface PendingAttachment {
  readonly localId: string
  readonly filename: string
  readonly sizeBytes: number
  readonly status: PendingAttachmentStatus
  readonly progress: number
  readonly error?: string
}
